import React, { useMemo } from "react";

import { PACKAGE_SELECTOR_PAGE_SIZE } from "constants/constants";
import usePaginatedList from "hooks/usePaginatedList";
import { getPackageLabel, PACKAGE_SEARCHABLE_FIELDS } from "utils/packageUtils";
import {
  EmptyState,
  Icon,
  IconButton,
  Loader,
  Pagination,
  QueryErrorPanel,
  SearchBar,
} from "widgets";

import styles from "./PackageSelector.module.scss";

const PackageSelector = ({
  selectAll = false,
  selectedIds = [],
  onChange,
  disabled = false,
  error,
  packages = [],
  isLoading = false,
  isError = false,
  fetchError = null,
  onRefetch,
}) => {
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    filteredItems: filteredPackages,
    paginatedItems: paginatedPackages,
    totalPages,
  } = usePaginatedList(packages, PACKAGE_SELECTOR_PAGE_SIZE, PACKAGE_SEARCHABLE_FIELDS);

  const selectedPackages = useMemo(
    () =>
      selectedIds
        .map((id) => packages.find((pkg) => pkg.packageId === id))
        .filter(Boolean),
    [packages, selectedIds]
  );

  const handlePackageApplyModeChange = (mode) => {
    if (disabled) return;
    if (mode === "all") {
      onChange({ selectAll: true, packageIds: [] });
      return;
    }
    onChange({ selectAll: false, packageIds: selectedIds });
  };

  const togglePackageSelection = (packageId) => {
    if (disabled || selectAll) return;

    const nextSelected = selectedIds.includes(packageId)
      ? selectedIds.filter((id) => id !== packageId)
      : [...selectedIds, packageId];

    onChange({ selectAll: false, packageIds: nextSelected });
  };

  const removeSelectedPackage = (packageId) => {
    if (disabled) return;
    onChange({
      selectAll: false,
      packageIds: selectedIds.filter((id) => id !== packageId),
    });
  };

  const clearAllSelectedPackages = () => {
    if (disabled) return;
    onChange({ selectAll: false, packageIds: [] });
  };

  if (isLoading) {
    return <Loader variant="inline" />;
  }

  if (isError) {
    return (
      <QueryErrorPanel
        message={fetchError?.message || "Failed to load packages."}
        onRetry={onRefetch}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.applyRow}>
        <span className={styles.applyLabel}>Apply to</span>
        <div className={styles.radioGroup}>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="packageApplyMode"
              checked={selectAll}
              onChange={() => handlePackageApplyModeChange("all")}
              disabled={disabled}
            />
            <span>All Packages</span>
          </label>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="packageApplyMode"
              checked={!selectAll}
              onChange={() => handlePackageApplyModeChange("selected")}
              disabled={disabled}
            />
            <span>Selected Packages</span>
          </label>
        </div>
      </div>

      <div className={styles.selectionPanel}>
        {selectAll ? (
          <div className={styles.selectionPanelAll}>
            <div className={styles.infoBanner}>
              <Icon name="info" fontSize="small" />
              <span>Coupon will be applicable to all packages from OMS.</span>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <SearchBar
                  value={searchTerm}
                  onChange={setSearchTerm}
                  searchableFields={PACKAGE_SEARCHABLE_FIELDS}
                  fieldLabels={{
                    displayName: "name",
                    packageName: "name",
                    packageId: "ID",
                  }}
                  disabled={disabled}
                />
              </div>
              <span className={styles.clearAllSlot}>
                <button
                  type="button"
                  className={styles.clearAllBtn}
                  onClick={clearAllSelectedPackages}
                  disabled={disabled || selectedIds.length === 0}
                  style={{
                    visibility: selectedIds.length > 0 ? "visible" : "hidden",
                  }}
                >
                  Clear All
                </button>
              </span>
            </div>

            <div className={styles.dualPane}>
              <div className={styles.browsePane}>
                <div className={styles.paneHeader}>
                  <h3>Browse Packages</h3>
                  <span>{filteredPackages.length} available</span>
                </div>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th />
                        <th>Package Name</th>
                        <th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPackages.length === 0 ? (
                        <tr>
                          <td colSpan={3} className={styles.emptyCell}>
                            <EmptyState message="No packages match your search." />
                          </td>
                        </tr>
                      ) : (
                        paginatedPackages.map((pkg) => {
                          const isSelected = selectedIds.includes(pkg.packageId);
                          return (
                            <tr
                              key={pkg.packageId}
                              className={isSelected ? styles.rowSelected : ""}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePackageSelection(pkg.packageId)}
                                  disabled={disabled}
                                />
                              </td>
                              <td>{getPackageLabel(pkg)}</td>
                              <td>{pkg.packageId}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={filteredPackages.length}
                  pageSize={PACKAGE_SELECTOR_PAGE_SIZE}
                  onPageChange={setCurrentPage}
                  disabled={disabled}
                  itemLabel="packages"
                />
              </div>

              <div className={styles.selectedPane}>
                <div className={styles.paneHeader}>
                  <h3>Selected Packages ({selectedPackages.length})</h3>
                </div>
                <div className={styles.selectedList}>
                  {selectedPackages.length === 0 ? (
                    <EmptyState message="No packages selected yet." />
                  ) : (
                    selectedPackages.map((pkg) => (
                      <div key={pkg.packageId} className={styles.selectedItem}>
                        <div>
                          <span className={styles.selectedName}>
                            {getPackageLabel(pkg)}
                          </span>
                          <span className={styles.selectedMeta}>
                            ID: {pkg.packageId}
                          </span>
                        </div>
                        <IconButton
                          iconName="close"
                          ariaLabel={`Remove ${getPackageLabel(pkg)}`}
                          className={styles.selectedRemove}
                          onClick={() => removeSelectedPackage(pkg.packageId)}
                          disabled={disabled}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className={styles.infoBanner}>
              <Icon name="info" fontSize="small" />
              <span>Coupon will be applicable only to the selected packages.</span>
            </div>
          </>
        )}
      </div>

      {error && <p className={styles.fieldError}>{error}</p>}
    </div>
  );
};

export default PackageSelector;
